import React, { useMemo } from 'react';
import { DropzoneState, FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box, BoxExtendedProps, HelpIcon, Hint, Preloader, ReceiptIcon, Text, useResponsive } from '@common-fe/common-fe';

import { MAX_SIZE_OF_FILE_IN_MEGABYTES } from '@/common/constants';

import {
  DropScanSubtitle2,
  DropScanTitle,
  DropScanTitlePrimary,
  DropScanWrapper,
  Wrapper,
} from './DropScan.styles';

interface Props extends Pick<DropzoneState, 'isDragActive' | 'isDragAccept' | 'isDragReject' | 'getRootProps' | 'getInputProps'> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  isCenterTitle?: boolean;
  isLoading?: boolean;
  wrapperProps?: BoxExtendedProps;
  contentWrapperProps?: BoxExtendedProps;
  additionalAction?: () => void;
  onFilesSelect?: () => void;
  onFileUploadError?: (error?: FileError) => void;
  contentRenderWrapper?: (isLoading?: boolean) => React.ReactNode;
}

export const DropScanContent: React.FC<Props> = ({
  icon,
  title,
  isCenterTitle,
  wrapperProps,
  contentWrapperProps,
  isLoading,
  isDragActive,
  isDragAccept,
  isDragReject,
  getRootProps,
  getInputProps,
  contentRenderWrapper,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const content = useMemo(() => {
    if (contentRenderWrapper) {
      return contentRenderWrapper(isLoading);
    }

    return (
      <>
        <Box margin={{ vertical: 'auto' }} justify="center" align="center">
          {!isLoading && <input {...getInputProps()} />}
          {isLoading ? (
            <Box height="120px" justify="center" align="center">
              <Preloader />
            </Box>
          ) : (
            icon || (
              <Box width="72px" height="72px" align="center" justify="center">
                <ReceiptIcon size="36px" color="iconAccent" />
              </Box>
            )
          )}
          {title || (
            <DropScanTitle style={isCenterTitle ? { textAlign: 'center' } : {}}>
              <p
                style={{
                  textAlign: isMobile ? 'center' : undefined,
                }}
              >
                {t('Drag or ')}
                <DropScanTitlePrimary>{t('browse')}</DropScanTitlePrimary> {t(' a picture of your receipt')}
              </p>
            </DropScanTitle>
          )}
          <DropScanSubtitle2>PDF, JPEG, JPG, PNG, TIFF, HEIC, HEIF</DropScanSubtitle2>
          <DropScanSubtitle2>Max. file size: {MAX_SIZE_OF_FILE_IN_MEGABYTES}MB</DropScanSubtitle2>
        </Box>

        <Box direction="row" align="center" gap="spacing4">
          <DropScanSubtitle2 martingTop="none">
            Receipt not in one of the allowed file types?
            <Hint
              style={{ display: 'inline', verticalAlign: 'middle' }}
              hintWidth={214}
              margin={{ left: 'spacing4' }}
              dropProps={{ style: { zIndex: 10000 } }}
              hintElementWrapperProps={{ style: { display: 'inline', verticalAlign: 'middle' }}}
              hintElement={<HelpIcon size="16px" color="iconDisabled" />}
            >
              <Text textAlign="center" size="small">
                {t('Please convert the image into an approved file type or ask your provider for an updated receipt as a PDF.')}
              </Text>
            </Hint>
          </DropScanSubtitle2>
        </Box>
      </>
    );
  }, [contentRenderWrapper, getInputProps, icon, isCenterTitle, isLoading, t, isMobile, title]);

  return (
    <Wrapper
      data-testid="drop_scan_wrapper"
      direction="column"
      background="module"
      round="moduleRound"
      pad="spacing24"
      {...wrapperProps}
    >
      <DropScanWrapper
        data-testid="drop_scan"
        style={{ justifyContent: 'center' }}
        background="canvas"
        round="container1Round"
        pad="spacing16"
        {...contentWrapperProps}
        {...getRootProps({
          isDragActive,
          isDragAccept,
          isDragReject,
        })}
      >
        {content}
      </DropScanWrapper>
    </Wrapper>
  );
};

export default DropScanContent;
